/**
 * Shared helpers for activity pax (participant) handling, used by the guest
 * booking engine and the staff activity/billing modals.
 *
 * Pricing rule: a per_pax activity bills for at least its `minimum_pax`, even when
 * fewer guests join. A per_unit activity bills for the units ordered; its min/max
 * pax are capacity copy and never bound or clamp anything.
 */

// Human-readable pax constraint, e.g. "2–6 guests" / "Minimum 2 guests".
export const paxLabel = (minPax, maxPax) => {
  if (minPax > 1 && maxPax > 0) return `${minPax}–${maxPax} guests`;
  if (minPax > 1) return `Minimum ${minPax} guests`;
  if (maxPax > 0) return `Up to ${maxPax} guests`;
  return "Any group size";
};

// Split inclusions/exclusions text into trimmed, non-empty lines.
export const toActivityLines = (text) =>
  (text || "").split("\n").map((s) => s.trim()).filter(Boolean);

export const isPerUnitActivity = (activity) => activity?.billing_mode === "per_unit";

// Mirrored server-side in hotel-booking/constants/activityLimits.ts.
export const MAX_ACTIVITY_UNITS = 99;

export const billedActivityCount = (activity, count) => {
  if (!count) return 0;
  if (isPerUnitActivity(activity)) return count;
  return Math.max(count, activity?.minimum_pax || 1);
};

export const activityLineTotal = (activity, billedCount) =>
  parseFloat(activity?.rate || 0) * (billedCount || 0);

export const activityQtyLabel = (activity, billedCount) =>
  isPerUnitActivity(activity)
    ? `${billedCount} ${billedCount === 1 ? "unit" : "units"}`
    : `${billedCount} pax`;

export const activityRateLabel = (activity) =>
  isPerUnitActivity(activity) ? "per unit" : "per person";

export const unitCapacityLabel = (maxPax) =>
  maxPax > 0 ? `Seats up to ${maxPax} per unit` : "Sold per unit";

export const activityStepperBounds = (activity) => {
  if (isPerUnitActivity(activity)) {
    return { min: 0, max: MAX_ACTIVITY_UNITS, jumpMin: 0 };
  }
  const maxPax = activity?.maximum_pax || 0;
  const minPax = activity?.minimum_pax || 1;
  return { min: 0, max: maxPax > 0 ? maxPax : 20, jumpMin: minPax };
};

// Next count for an activity stepper. The first increment jumps 0 -> minPax,
// decrementing at minPax clears to 0, and the count never exceeds maxPax.
// Returns the count unchanged when a bound is hit.
export const stepPaxCount = (current, minPax, maxPax, increment) => {
  const min = minPax || 1;
  const max = maxPax || 0;
  if (increment) {
    const next =
      current === 0 && min > 1 ? (max > 0 ? Math.min(min, max) : min) : current + 1;
    return max > 0 && next > max ? current : next;
  }
  if (current <= 0) return 0;
  return min > 1 && current <= min ? 0 : current - 1;
};
